import { UserType, type UserRole } from "@/shared/types/user.entity";

export type SocketEventDescriptor = {
  name: string;
  /** Optional fine-grained gate: only forward to roles in the list. Undefined = all roles in this user_type. */
  requiredRoles?: readonly UserRole[];
};

export type NotificationSource = {
  /**
   * Master switch. If false: bell does not render, hook returns empty state, no REST or socket forwarding for
   * this user_type.
   */
  enabled: boolean;
  /** REST endpoint that feeds the bell on mount. Null when disabled. */
  restListEndpoint: string | null;
  restMarkReadEndpoint: ((id: string) => string) | null;
  /** Events forwarded from the socket to UI listeners. Anything outside this list is dropped client-side. */
  socketEvents: readonly SocketEventDescriptor[];
  /** Informative only — the actual room join happens server-side at handshake. */
  socketAudienceRoom: string | null;
};

export const NOTIFICATION_SOURCES: Record<UserType, NotificationSource> = {
  [UserType.INTERNAL]: {
    enabled: true,
    restListEndpoint: "/internal/operations/notifications",
    restMarkReadEndpoint: (id) => `/internal/operations/notifications/${encodeURIComponent(id)}/read`,
    socketEvents: [
      { name: "payment:created" },
      { name: "payment:cancelled" },
      { name: "payment:updated" },
      { name: "payment:inProgress" },
      { name: "payment:filed" },
      { name: "confirmation:created" },
      { name: "confirmation:updated" },
      { name: "confirmation:notified" },
      { name: "note:created" },
      { name: "notification:created" },
      { name: "operation.confirmation.takerReminder" },
      { name: "operation.payment.takerReminder" },
    ],
    socketAudienceRoom: "audience:internal",
  },
  [UserType.ORGANIZATION_MEMBER]: {
    enabled: false,
    restListEndpoint: null,
    restMarkReadEndpoint: null,
    // future: chat events go here (e.g. "chat:message:new", "chat:typing")
    socketEvents: [],
    socketAudienceRoom: null, // future: `audience:org:${organizationId}`
  },
};

export function resolveNotificationSource(userType: UserType): NotificationSource {
  return NOTIFICATION_SOURCES[userType];
}

export function shouldForwardSocketEvent(
  eventName: string,
  user: { user_type: UserType; role: UserRole },
): boolean {
  const source = NOTIFICATION_SOURCES[user.user_type];
  if (!source.enabled) return false;
  const event = source.socketEvents.find((e) => e.name === eventName);
  if (!event) return false;
  if (event.requiredRoles && !event.requiredRoles.includes(user.role)) return false;
  return true;
}
